import { useEffect, useRef } from 'react';
import { CRASH_GROWTH_K, crashDurationMs } from '@raketta/shared';
import { Icon } from '../components/Icon';
import { telegram } from '../lib/telegram';
import { formatTon } from '../lib/format';
import { replayLink } from '../lib/appConfig';
import { sound } from '../lib/sound';

interface Props {
  betId: number;
  name: string;
  crashPoint: number;
  cashMultiplier: number | null;
  totalWin: string;
  onBack: () => void;
}

// Самопроигрывающийся реплей крэш-раунда: кривая до точки + метка кэшаута.
export function CrashReplay({ betId, name, crashPoint, cashMultiplier, totalWin, onBack }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const crashDur = crashDurationMs(crashPoint);
    const replayTotal = Math.min(Math.max(crashDur, 1800), 5000);
    const speed = crashDur > 0 ? crashDur / replayTotal : 1;
    const start = performance.now();
    sound.resume();
    sound.spin();
    let crashed = false;
    let raf = 0;

    const draw = (now: number) => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const real = now - start;
      let m = Math.exp(CRASH_GROWTH_K * ((real * speed) / 1000));
      if (m >= crashPoint) {
        m = crashPoint;
        if (!crashed) {
          crashed = true;
          sound.cascade(6);
          telegram.haptic('heavy');
        }
      }

      ctx.fillStyle = '#0b0f1a';
      ctx.fillRect(0, 0, w, h);

      const padL = 14, padB = 22, padT = 14;
      const plotW = w - padL - 14;
      const plotH = h - padB - padT;
      const tNow = Math.max(0.0001, Math.log(m) / CRASH_GROWTH_K);
      const maxT = Math.max(tNow, 2.2);
      const maxY = Math.max(m - 1, 0.6);
      const X = (t: number) => padL + (t / maxT) * plotW;
      const Y = (mm: number) => padT + plotH - ((mm - 1) / maxY) * plotH;
      const col = crashed ? '#ff4d5e' : m >= 10 ? '#ffd24a' : '#18d6ff';

      ctx.beginPath();
      const steps = 60;
      for (let i = 0; i <= steps; i++) {
        const t = (tNow * i) / steps;
        const mm = Math.exp(CRASH_GROWTH_K * t);
        const px = X(t), py = Y(mm);
        if (i) ctx.lineTo(px, py);
        else ctx.moveTo(px, py);
      }
      ctx.strokeStyle = col;
      ctx.lineWidth = 3;
      ctx.shadowColor = col;
      ctx.shadowBlur = 12;
      ctx.stroke();
      ctx.shadowBlur = 0;

      if (cashMultiplier && cashMultiplier <= m) {
        const tc = Math.log(cashMultiplier) / CRASH_GROWTH_K;
        const cx = X(tc), cy = Y(cashMultiplier);
        ctx.fillStyle = '#00e701';
        ctx.beginPath();
        ctx.arc(cx, cy, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = '700 12px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`×${cashMultiplier.toFixed(2)}`, cx, cy - 10);
      }

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = crashed ? '#ff4d5e' : '#fff';
      ctx.font = '900 46px Inter, sans-serif';
      ctx.shadowColor = crashed ? '#ff4d5e' : '#000';
      ctx.shadowBlur = crashed ? 16 : 6;
      ctx.fillText('×' + m.toFixed(2), w / 2, h / 2);
      ctx.shadowBlur = 0;

      if (!crashed) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [crashPoint, cashMultiplier]);

  return (
    <>
      <div className="page-head">
        <button className="icon-btn" onClick={onBack} title="Назад">
          <Icon name="back" size={18} />
        </button>
        <span className="title">Реплей · Ракетка {name}</span>
      </div>
      <div className="card crash-stage">
        <canvas ref={canvasRef} className="crash-canvas" />
      </div>
      <div className="card replay-foot">
        <span className="replay-tag">
          {cashMultiplier
            ? `ЗАБРАЛ ×${cashMultiplier.toFixed(2)} · +${formatTon(totalWin)}`
            : `КРАШ ×${crashPoint.toFixed(2)}`}
        </span>
        <button
          className="btn"
          onClick={() =>
            telegram.share(
              `Зацени реплей: ×${(cashMultiplier ?? crashPoint).toFixed(2)} в Ракетке — 1Zov`,
              replayLink(betId)
            )
          }
        >
          Поделиться
        </button>
        <button className="btn ghost" onClick={onBack}>
          Назад
        </button>
      </div>
    </>
  );
}
